import { useImperativeHandle, useRef, type Ref } from 'react';
import { Button, ActionIcon, Menu, Group, Box } from '@mantine/core';
import { IconPlus, IconAdjustments, IconCheck, IconRestore } from '@tabler/icons-react';
import type { LayoutSection } from '@/hooks/useDashboardLayout';

export interface HiddenItem {
  section: LayoutSection;
  id: string;
  label: string;
}

const SECTIONS: Array<{ section: LayoutSection; heading: string }> = [
  { section: 'tiles', heading: 'Tiles' },
  { section: 'charts', heading: 'Charts' },
];

/** Imperative surface for callers that must restore focus after an item unmounts. */
export interface DashboardEditControlsHandle {
  /**
   * Move keyboard focus to the Customize/Done toggle. Tries the desktop
   * button first; when it is display:none (below the `sm` breakpoint,
   * `visibleFrom`), `.focus()` silently no-ops, so fall back to the mobile
   * ActionIcon — whichever variant is actually visible receives focus.
   */
  focusDone: () => void;
}

export interface DashboardEditControlsProps {
  editMode: boolean;
  onToggleEditMode: () => void;
  hiddenItems: HiddenItem[];
  onRestore: (section: LayoutSection, id: string) => void;
  onReset: () => void;
  isCustomized: boolean;
  /** Handle used to restore focus after an item is removed (see ChartsPage handleRemove) */
  controlsRef?: Ref<DashboardEditControlsHandle>;
}

/**
 * Dashboard header controls for edit ("Customize") mode:
 * - Customize/Done toggle (labeled button on desktop, icon-only on mobile)
 * - "Add" menu listing hidden tiles/charts, shown only while editing
 * - "Reset layout", shown only while editing a customized layout
 */
export function DashboardEditControls({
  editMode,
  onToggleEditMode,
  hiddenItems,
  onRestore,
  onReset,
  isCustomized,
  controlsRef,
}: DashboardEditControlsProps): JSX.Element {
  const desktopToggleRef = useRef<HTMLButtonElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);

  useImperativeHandle(controlsRef, () => ({
    focusDone: () => {
      desktopToggleRef.current?.focus();
      if (document.activeElement !== desktopToggleRef.current) {
        // Desktop button is display:none (mobile viewport) — focus no-oped.
        mobileToggleRef.current?.focus();
      }
    },
  }));

  return (
    <Group gap="xs" wrap="wrap">
      {editMode && hiddenItems.length > 0 && (
        <Menu width={220}>
          <Menu.Target>
            <Button variant="light" size="xs" leftSection={<IconPlus size={16} />}>
              Add
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {SECTIONS.map(({ section, heading }) => {
              const items = hiddenItems.filter(i => i.section === section);
              if (items.length === 0) return null;
              return (
                <Box key={section}>
                  <Menu.Label>{heading}</Menu.Label>
                  {items.map(item => (
                    <Menu.Item key={item.id} onClick={() => onRestore(section, item.id)}>
                      {item.label}
                    </Menu.Item>
                  ))}
                </Box>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      )}
      {editMode && isCustomized && (
        <Button
          variant="subtle"
          color="gray"
          size="xs"
          leftSection={<IconRestore size={16} />}
          onClick={onReset}
        >
          Reset layout
        </Button>
      )}
      {/* Desktop: labeled button */}
      <Box visibleFrom="sm">
        <Button
          ref={desktopToggleRef}
          variant={editMode ? 'filled' : 'default'}
          size="xs"
          leftSection={editMode ? <IconCheck size={16} /> : <IconAdjustments size={16} />}
          onClick={onToggleEditMode}
          aria-label={editMode ? 'Done customizing' : 'Customize dashboard'}
        >
          {editMode ? 'Done' : 'Customize'}
        </Button>
      </Box>
      {/* Mobile: icon-only. No Tooltip: it can overlap/clip the edit-mode
          hint text below at narrow widths on mouse-hover, tooltips never
          show on touch anyway, and the aria-label carries the accessible
          name. */}
      <Box hiddenFrom="sm">
        <ActionIcon
          ref={mobileToggleRef}
          variant={editMode ? 'filled' : 'subtle'}
          color="gray"
          size="lg"
          onClick={onToggleEditMode}
          aria-label={editMode ? 'Done customizing' : 'Customize dashboard'}
        >
          {editMode ? <IconCheck size={16} /> : <IconAdjustments size={16} />}
        </ActionIcon>
      </Box>
    </Group>
  );
}
